import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { DSCountIndicator, DSIcon } from '../design-system/primitives';
import './Navigation.css';
import { authService } from '../services/authService';
import { apiService } from '../services/apiService';
import { websocketService } from '../services/websocketService';
import PinballMachineIcon from './PinballMachineIcon';
import { isEventInProgress } from '../utils/eventHelpers';

const isPingolfEvent = (event) => event?.event_metadata?.pinball_format === 'pingolf';

const isActivePingolfEvent = (event) =>
  isPingolfEvent(event) && isEventInProgress(event);

function Navigation({ onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [notificationError, setNotificationError] = useState('');
  const [activePingolfEvents, setActivePingolfEvents] = useState([]);
  const notificationPanelRef = useRef(null);

  const isActive = (path) => {
    return location.pathname === path ? 'active' : '';
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Get current user to determine role
  useEffect(() => {
    const user = authService.getCurrentUser();
    setCurrentUser(user);
    if (user) {
      loadUnreadCount();
      loadActivePingolfEvents();
    }
  }, []);

  useEffect(() => {
    const unsubscribe = websocketService.on('notification_created', (notification) => {
      setUnreadCount(prev => prev + 1);
      if (isNotificationOpen && notification) {
        setNotifications(prev => [notification, ...prev]);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [isNotificationOpen]);

  // Close menu when route changes
  useEffect(() => {
    setIsOpen(false);
    setIsNotificationOpen(false);
  }, [location.pathname]);

  // Close menu when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      const nav = document.querySelector('.navigation');
      const toggle = document.querySelector('.mobile-menu-toggle');
      if (isOpen && nav && (!toggle || !toggle.contains(event.target)) && !nav.contains(event.target)) {
        setIsOpen(false);
      }

      if (isNotificationOpen && notificationPanelRef.current && !notificationPanelRef.current.contains(event.target)) {
        setIsNotificationOpen(false);
      }
    };

    if (isOpen || isNotificationOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isNotificationOpen]);

  const loadNotifications = async () => {
    try {
      setLoadingNotifications(true);
      setNotificationError('');
      const response = await apiService.getNotifications({ limit: 25 });
      setNotifications(response.data || []);
    } catch (err) {
      console.error('Failed to load notifications:', err);
      setNotificationError('Unable to load notifications');
    } finally {
      setLoadingNotifications(false);
    }
  };

  const loadActivePingolfEvents = async () => {
    try {
      const response = await apiService.get('/events');
      const nextEvents = (response.data || []).filter(isActivePingolfEvent);
      setActivePingolfEvents(nextEvents);
    } catch (err) {
      console.error('Failed to load active PinGolf events:', err);
      setActivePingolfEvents([]);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await apiService.getUnreadNotificationCount();
      setUnreadCount(response.data?.count || 0);
    } catch (err) {
      console.error('Failed to load notification count:', err);
    }
  };

  const handleToggleNotifications = async () => {
    const nextOpenState = !isNotificationOpen;
    setIsNotificationOpen(nextOpenState);

    if (!nextOpenState) {
      return;
    }

    await loadNotifications();

    if (unreadCount > 0) {
      try {
        await apiService.markAllNotificationsRead();
        setUnreadCount(0);
        setNotifications(prev => prev.map(item => ({ ...item, is_read: true })));
      } catch (err) {
        console.error('Failed to mark notifications as read:', err);
      }
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await apiService.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(item => item.id !== notificationId));
    } catch (err) {
      console.error('Failed to delete notification:', err);
      setNotificationError('Unable to delete notification');
    }
  };

  const handleClearNotifications = async () => {
    try {
      await apiService.clearNotifications();
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to clear notifications:', err);
      setNotificationError('Unable to clear notifications');
    }
  };

  const getNotificationPath = (notification) => {
    const metadata = notification?.metadata;
    if (metadata && typeof metadata === 'object' && typeof metadata.path === 'string') {
      return metadata.path;
    }

    if (metadata && typeof metadata === 'object' && metadata.type === 'event_rsvp') {
      const eventId = metadata.event_id || metadata.eventId;
      if (eventId) {
        return `/events/${eventId}`;
      }
    }

    if (metadata && typeof metadata === 'object' && metadata.type === 'machine_issue_assigned' && metadata.issueId) {
      return `/machine-issues/${metadata.issueId}`;
    }

    return null;
  };

  const handleNotificationClick = async (notification) => {
    const path = getNotificationPath(notification);
    if (!path) {
      return;
    }

    try {
      if (!notification.is_read) {
        await apiService.markNotificationRead(notification.id);
        setNotifications((prev) => prev.map((item) => (
          item.id === notification.id ? { ...item, is_read: true } : item
        )));
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }

    setIsNotificationOpen(false);
    setIsOpen(false);
    navigate(path);
  };

  const formatNotificationDate = (dateString) => {
    if (!dateString) {
      return '';
    }
    return new Date(dateString).toLocaleString();
  };

  const isAdmin = currentUser?.role === 'admin';
  const isMachinesActive = location.pathname === '/machines' || location.pathname.startsWith('/machines/');
  const isMyMembershipActive = location.pathname === '/my-membership' || location.pathname.startsWith('/my-membership/');
  const pingolfNavIsActive = location.pathname === '/pingolf'
    || location.pathname.startsWith('/pingolf-season')
    || location.pathname.endsWith('/host-controls')
    || activePingolfEvents.some(event => location.pathname === `/events/${event.id}`);

  // Lands on the League screen rather than the events list: the league (standings, next event,
  // prize pool) is the front door for a member, and the next event is one tap from there.
  const handlePingolfNavigation = () => {
    setIsOpen(false);
    navigate('/pingolf-season');
  };

  return (
    <>
      {/* Mobile Header Bar - Only visible on mobile */}
      <div className="mobile-header-bar">
        <button className="mobile-menu-toggle" onClick={toggleMenu} aria-label="Toggle menu">
          <DSIcon name="menu" />
        </button>
        <div className="mobile-branding">
          <img src="/logo.png" alt="Capital Pinball" className="mobile-logo" />
          <h1 className="mobile-app-title">Capital Pinball</h1>
        </div>
        <div className="mobile-notification-wrapper" ref={notificationPanelRef}>
          <button
            type="button"
            className={`mobile-notification-bell ${unreadCount > 0 ? 'has-unread' : ''}`}
            onClick={handleToggleNotifications}
            aria-label="Notifications"
          >
            <span className="mobile-notification-icon"><DSIcon name="bell" filled /></span>
            {unreadCount > 0 && (
              <DSCountIndicator count={unreadCount} label="unread notifications" />
            )}
          </button>

          {isNotificationOpen && (
            <div className="mobile-notification-panel">
              <div className="mobile-notification-panel-header">
                <h3>Notifications</h3>
                <button
                  type="button"
                  className="mobile-notification-clear"
                  onClick={handleClearNotifications}
                  disabled={notifications.length === 0}
                >
                  Clear All
                </button>
              </div>

              {loadingNotifications ? (
                <div className="mobile-notification-empty">Loading notifications...</div>
              ) : notificationError ? (
                <div className="mobile-notification-empty">{notificationError}</div>
              ) : notifications.length === 0 ? (
                <div className="mobile-notification-empty">No notifications</div>
              ) : (
                <div className="mobile-notification-list">
                  {notifications.map(notification => (
                    <div key={notification.id} className="mobile-notification-item">
                      <div
                        className={`mobile-notification-content ${getNotificationPath(notification) ? 'is-clickable' : ''}`}
                        onClick={() => handleNotificationClick(notification)}
                        role={getNotificationPath(notification) ? 'button' : undefined}
                        tabIndex={getNotificationPath(notification) ? 0 : undefined}
                        onKeyDown={(event) => {
                          if (!getNotificationPath(notification)) {
                            return;
                          }

                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleNotificationClick(notification);
                          }
                        }}
                      >
                        <p>{notification.message}</p>
                        <small>{formatNotificationDate(notification.created_at)}</small>
                      </div>
                      <button
                        type="button"
                        className="mobile-notification-delete"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDeleteNotification(notification.id);
                        }}
                        aria-label="Delete notification"
                      >
                        <DSIcon name="x" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Overlay for mobile menu */}
      {isOpen && <div className="mobile-menu-overlay" onClick={() => setIsOpen(false)}></div>}

      <nav className={`navigation ${isOpen ? 'open' : ''}`}>

      <ul className="nav-menu">
        <li>
          <Link to="/my-membership" className={isMyMembershipActive ? 'active' : ''}>
            <span className="icon"><DSIcon name="user" /></span>
            My Account
          </Link>
        </li>
        <li>
          <Link to="/scorekeeping/leaderboard" className={isActive('/scorekeeping/leaderboard')}>
            <span className="icon"><DSIcon name="trophy" /></span>
            Leaderboard
          </Link>
        </li>
        <li>
          <Link to="/my-competition" className={isActive('/my-competition')}>
            <span className="icon"><DSIcon name="swords" /></span>
            Transformers
          </Link>
        </li>
        <li>
          <Link to="/events" className={isActive('/events')}>
            <span className="icon"><DSIcon name="sparkles" /></span>
            Events
          </Link>
        </li>
        <li>
          <Link to="/tasks" className={isActive('/tasks')}>
            <span className="icon"><DSIcon name="checkCircle" /></span>
            Tasks
          </Link>
        </li>
        {/* Open to every signed-in member. This used to require isAdmin OR a PinGolf event that
            was live at that exact moment, so a member had no way in outside event hours - they
            could not reach league standings, upcoming nights, or their own card history. The
            routes behind it are member-facing; only the league admin pages are role-guarded. */}
        <li>
          <button
            type="button"
            className={`nav-menu-button ${pingolfNavIsActive ? 'active' : ''}`}
            onClick={handlePingolfNavigation}
          >
            <span className="icon"><DSIcon name="golf" /></span>
            PinGolf
          </button>
        </li>
        <li>
          <Link to="/machines" className={isMachinesActive ? 'active' : ''}>
            <span className="icon"><PinballMachineIcon size={20} /></span>
            Machines
          </Link>
        </li>
        <li>
          <Link to="/help" className={isActive('/help')}>
            <span className="icon"><DSIcon name="question" /></span>
            Help
          </Link>
        </li>

        {isAdmin && (
          <>
            {/* Leaderboard Admin is deliberately not here. It is reached from a
                Manage button on the leaderboard itself, which is the page an
                admin is already looking at when they decide something needs
                changing, and it keeps a tool nobody else can use out of the
                navigation everyone shares. */}
            {/* Club Screen is not here either. It lives with the other
                competition tools, on the leaderboard admin screen. It is a thing
                you open once to check the TV, not somewhere you navigate to. */}
            <li>
              <Link to="/users" className={isActive('/users')}>
                <span className="icon"><DSIcon name="users" /></span>
                User Management
              </Link>
            </li>
            <li>
              <Link to="/rules" className={isActive('/rules')}>
                <span className="icon"><DSIcon name="scroll" /></span>
                Rules and Agreements
              </Link>
            </li>
            <li>
              <Link to="/developer" className={isActive('/developer')}>
                <span className="icon"><DSIcon name="wrench" /></span>
                Developer Tools
              </Link>
            </li>
          </>
        )}
      </ul>

      <div className="nav-footer">
        <Link to="/change-password" className="nav-footer-link">
          <span className="icon"><DSIcon name="key" /></span>
          Change Password
        </Link>
        <button onClick={onLogout} className="logout-button">
          <span className="icon"><DSIcon name="logout" /></span>
          Logout
        </button>
      </div>
    </nav>
    </>
  );
}

export default Navigation;
